import { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUpRight, X } from "lucide-react";
import CapabilityTabs from "@/components/site/CapabilityTabs";
import PageHero from "@/components/site/PageHero";
import { Reveal } from "@/components/site/Reveal";
import { PRODUCT_CATEGORIES } from "@/data/products";

function ProductCard({ product, index, onOpen }) {
  return (
    <Reveal delay={(index % 4) * 0.06}>
      <button
        type="button"
        onClick={() => onOpen(product)}
        data-testid={`product-card-${index}`}
        className="group flex w-full flex-col text-left border-r border-b border-border bg-white hover:bg-secondary transition-colors focus:outline-none"
      >
        <div className="flex items-baseline justify-between gap-3 px-5 pt-5 pb-3">
          <h3 className="font-display font-medium text-base md:text-lg tracking-tight leading-snug">
            {product.name}
          </h3>
          <span className="font-mono-tech text-[10px] text-[hsl(var(--signal))] shrink-0">
            {String(index + 1).padStart(2, "0")}
          </span>
        </div>
        <div className="relative overflow-hidden bg-secondary aspect-[4/3]">
          <img
            src={product.image}
            alt={product.name}
            loading="lazy"
            className="h-full w-full object-cover transition-transform duration-[900ms] ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:scale-105"
          />
          <span className="absolute top-3 right-3 grid h-8 w-8 place-items-center bg-white/90 backdrop-blur opacity-0 -translate-y-1 group-hover:opacity-100 group-hover:translate-y-0 transition-all">
            <ArrowUpRight className="h-4 w-4 text-[hsl(var(--signal))]" />
          </span>
        </div>
      </button>
    </Reveal>
  );
}

function EmptyState({ name }) {
  return (
    <div
      data-testid="products-empty-state"
      className="border border-dashed border-border bg-secondary/40 px-8 py-12 text-center"
    >
      <p className="font-mono-tech text-[11px] uppercase tracking-[0.3em] text-muted-foreground">
        [ Coming Soon ]
      </p>
      <p className="mt-4 text-muted-foreground max-w-md mx-auto leading-relaxed">
        Product imagery for <span className="text-foreground font-medium">{name}</span> is being
        prepared and will be published shortly.
      </p>
    </div>
  );
}

export default function Products() {
  const [lightbox, setLightbox] = useState(null);

  return (
    <div data-testid="products-page">
      <PageHero
        index="/ 03.2"
        overline="Capabilities — Products"
        title={<>What we build,<br />by domain.</>}
        subtitle="The categories below showcase our core expertise. We also provide bespoke manufacturing of custom components and full assemblies based on client requirements, engineering drawings, and technical specifications."
      />
      <CapabilityTabs />

      {/* Category quick-nav */}
      <div className="border-b border-border bg-white/85 backdrop-blur-xl">
        <div className="mx-auto max-w-[1500px] px-6 md:px-12">
          <div className="flex gap-6 overflow-x-auto py-4">
            {PRODUCT_CATEGORIES.map((c) => (
              <a
                key={c.id}
                href={`#${c.id}`}
                data-testid={`product-nav-${c.id}`}
                className="whitespace-nowrap font-mono-tech text-[11px] uppercase tracking-[0.18em] text-muted-foreground hover:text-[hsl(var(--signal))] transition-colors"
              >
                {c.name}
              </a>
            ))}
          </div>
        </div>
      </div>

      {/* Category sections */}
      <div className="mx-auto max-w-[1500px] px-6 md:px-12 py-12 md:py-16 space-y-20 md:space-y-28">
        {PRODUCT_CATEGORIES.map((category, ci) => (
          <section
            key={category.id}
            id={category.id}
            data-testid={`product-category-${category.id}`}
            className="scroll-mt-[180px]"
          >
            <Reveal>
              <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-4 lg:gap-10 mb-10 border-b border-border pb-6">
                <div className="lg:max-w-2xl">
                  <span className="font-mono-tech text-[11px] text-[hsl(var(--signal))]">
                    {String(ci + 1).padStart(2, "0")}
                  </span>
                  <h2 className="mt-2 font-display font-medium tracking-tight text-3xl md:text-5xl leading-[1.02]">
                    {category.name}
                  </h2>
                </div>
                {category.description && (
                  <p className="lg:max-w-sm lg:text-right text-muted-foreground leading-relaxed lg:pb-1">
                    {category.description}
                  </p>
                )}
              </div>
            </Reveal>

            {category.products.length ? (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 border-t border-l border-border">
                {category.products.map((p, i) => (
                  <ProductCard key={p.name} product={p} index={i} onOpen={setLightbox} />
                ))}
              </div>
            ) : (
              <EmptyState name={category.name} />
            )}
          </section>
        ))}
      </div>

      {/* Custom drawings CTA */}
      <section className="border-t border-border bg-foreground text-background" data-testid="products-custom-cta">
        <div className="mx-auto max-w-[1500px] px-6 md:px-12 py-12 md:py-16">
          <Reveal>
            <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-8">
              <div>
                <span className="font-mono-tech text-[11px] uppercase tracking-[0.3em] text-background/50">
                  [ Custom Manufacturing ]
                </span>
                <h2 className="mt-5 font-display font-medium tracking-tight text-3xl md:text-5xl leading-[1.05] max-w-2xl">
                  Don't see what you need? We manufacture to custom drawings.
                </h2>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Lightbox */}
      <AnimatePresence>
        {lightbox && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="fixed inset-0 z-[100] grid place-items-center bg-black/80 backdrop-blur-sm p-6"
            onClick={() => setLightbox(null)}
            data-testid="product-lightbox"
          >
            <button
              onClick={() => setLightbox(null)}
              data-testid="product-lightbox-close"
              className="absolute top-6 right-6 grid h-11 w-11 place-items-center bg-white/10 text-white hover:bg-white/20 transition-colors"
              aria-label="Close"
            >
              <X className="h-5 w-5" />
            </button>
            <motion.div
              initial={{ scale: 0.92, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.92, opacity: 0 }}
              transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
              className="max-w-4xl w-full bg-white"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="bg-secondary">
                <img
                  src={lightbox.image}
                  alt={lightbox.name}
                  className="w-full max-h-[75vh] object-contain"
                />
              </div>
              <div className="flex items-center justify-between gap-4 px-6 py-5 border-t border-border">
                <h3 className="font-display font-medium text-xl md:text-2xl tracking-tight">
                  {lightbox.name}
                </h3>
                <span className="font-mono-tech text-[11px] uppercase tracking-[0.2em] text-[hsl(var(--signal))]">
                  Krystal Technology
                </span>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
